import { accounts, products, solutions } from "@/assets/js/profile";

// 官网统计独立于旧 report 和百度统计。只发预定义类别，不发账号、IP、URL参数或留言内容。
const API =
  (process.env.VUE_APP_BASE_API || "").replace(/\/$/, "") + "/web/site/events";
const ID_KEY = "site-visitor-v1";
const SESSION_KEY = "site-session-v1";
const SESSION_TTL = 30 * 60 * 1000;
const ID_PATTERN = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;
let visitorId;
let session;
let currentPath;
let routeKey;
let queue = [];
let flushTimer;
let observer;
let mutationObserver;
let scanTimer;
let exposed = new Set();
const tracked = new WeakMap();

function uuid() {
  if (window.crypto && window.crypto.randomUUID)
    return window.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = Math.floor(Math.random() * 16);
    return (c === "x" ? r : (r & 3) | 8).toString(16);
  });
}

function read(storage, key) {
  try {
    return storage.getItem(key);
  } catch (e) {
    return null;
  }
}
function write(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch (e) {
    /* 禁用存储时使用当前页面内的匿名标识 */
  }
}
function source() {
  try {
    const tagged = new URL(window.location.href).searchParams.get("utm_source");
    if (tagged) {
      const value = tagged.toLowerCase();
      if (/wechat|weixin|wx|公众号|社群/.test(value)) return "wechat";
      if (/csdn/.test(value)) return "csdn";
      if (/github/.test(value)) return "github";
      if (/gitee/.test(value)) return "gitee";
      return "campaign";
    }
    if (!document.referrer) return "direct";
    const host = new URL(document.referrer).hostname;
    if (
      host === window.location.hostname ||
      /^(www\.|xcx\.)?88688\.team$/.test(host)
    )
      return "direct";
    if (
      /(^|\.)(baidu\.com|bing\.com|google\.[a-z.]+|sogou\.com|so\.com)$/.test(
        host
      )
    )
      return "search";
    if (/(^|\.)weixin\.qq\.com$/.test(host)) return "wechat";
    if (/(^|\.)csdn\.net$/.test(host)) return "csdn";
    if (host === "github.com") return "github";
    if (host === "gitee.com") return "gitee";
    return "referral";
  } catch (e) {
    return "direct";
  }
}

function getIdentity() {
  // getter 本身也可能因浏览器隐私设置抛错。
  let local, tab;
  try {
    local = window.localStorage;
    tab = window.sessionStorage;
  } catch (e) {
    /* 使用内存 */
  }
  if (!visitorId) {
    const stored = local && read(local, ID_KEY);
    visitorId = ID_PATTERN.test(stored || "") ? stored : uuid();
    if (local) write(local, ID_KEY, visitorId);
  }
  if (!session && tab) {
    try {
      session = JSON.parse(read(tab, SESSION_KEY));
    } catch (e) {
      /* 新会话 */
    }
  }
  if (
    !session ||
    !ID_PATTERN.test(session.id || "") ||
    !Number.isFinite(session.activeAt) ||
    ![
      "search",
      "wechat",
      "csdn",
      "github",
      "gitee",
      "campaign",
      "referral",
      "direct"
    ].includes(session.source) ||
    Date.now() - session.activeAt > SESSION_TTL
  ) {
    session = { id: uuid(), source: source(), activeAt: Date.now() };
  }
  session.activeAt = Date.now();
  if (tab) write(tab, SESSION_KEY, JSON.stringify(session));
  return { visitorId, sessionId: session.id, source: session.source };
}

export function productTarget(name) {
  const product = products.find(p => p.name === name);
  return (product && product.analyticsId) || null;
}

function classify(node) {
  if (node.tagName !== "A" && node.dataset && node.dataset.siteEntry)
    return { target: node.dataset.siteEntry };
  if (node.tagName === "IMG" && node.hasAttribute("data-zoom")) {
    const src = node.getAttribute("src");
    const p = products.find(
      item => item.analyticsId && item.qrcode && item.qrcode === src
    );
    if (p) return { type: "qr_open", target: p.analyticsId };
    const a = accounts.find(
      item => item.analyticsId && item.qrcode && item.qrcode === src
    );
    if (a) return { type: "qr_open", target: a.analyticsId };
  }
  if (node.tagName !== "A") return null;
  const href = node.getAttribute("href") || "";
  if (/^tel:/i.test(href)) return { type: "contact_click", target: "phone" };
  if (/^mailto:/i.test(href)) return { type: "contact_click", target: "email" };
  const s = solutions.find(item => item.analyticsId && item.demo === href);
  if (s) return { type: "demo_click", target: s.analyticsId };
  try {
    const link = new URL(href, window.location.href);
    if (
      link.hostname === "github.com" &&
      /\/mxx1111\/xuantuotuoV2\/?$/.test(link.pathname)
    )
      return { type: "outbound_click", target: "repo_xuantuotuo" };
    if (link.hostname === "github.com")
      return { type: "outbound_click", target: "github" };
    if (link.hostname === "gitee.com")
      return { type: "outbound_click", target: "gitee" };
    if (link.hostname === "blog.csdn.net")
      return { type: "outbound_click", target: "csdn" };
    if (
      link.hostname === "sdruizer.com" ||
      link.hostname === "www.sdruizer.com"
    )
      return { type: "outbound_click", target: "company" };
  } catch (e) {
    /* 非链接 */
  }
  return null;
}

export function trackSiteEvent(type, target = "") {
  if (!currentPath) return;
  const identity = getIdentity();
  queue.push({
    ...identity,
    event: { eventId: uuid(), type, target, path: currentPath }
  });
  // 内存队列有上限，统计失败不会持续占用页面资源。
  if (queue.length > 100) queue = queue.slice(-100);
  clearTimeout(flushTimer);
  flushTimer = setTimeout(() => flush(), 250);
}

function flush(beacon = false) {
  if (!queue.length) return;
  const first = queue[0];
  const batch = [];
  while (
    queue.length &&
    batch.length < 20 &&
    queue[0].sessionId === first.sessionId
  )
    batch.push(queue.shift().event);
  const body = JSON.stringify({
    visitorId: first.visitorId,
    sessionId: first.sessionId,
    source: first.source,
    events: batch
  });
  if (beacon && navigator.sendBeacon) {
    try {
      if (
        navigator.sendBeacon(
          API,
          new Blob([body], { type: "application/json" })
        )
      ) {
        if (queue.length) flush(true);
        return;
      }
    } catch (e) {
      /* fetch 兜底 */
    }
  }
  // 不携带登录凭据，失败静默处理；keepalive 保障外链跳转时尽可能完成上报。
  try {
    window
      .fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
        credentials: "omit"
      })
      .catch(() => {});
  } catch (e) {
    /* 统计不影响旧浏览器的正常业务 */
  }
  if (queue.length) flush(beacon);
}

function scan() {
  if (!observer || !currentPath) return;
  document
    .querySelectorAll("a[href],img[data-zoom],[data-site-entry]")
    .forEach(node => {
      const item = classify(node);
      if (!item) return;
      tracked.set(node, item);
      observer.observe(node);
    });
}

function startPage(path) {
  if (routeKey === path) return;
  flush();
  routeKey = path;
  currentPath = /^\/articles\/[^/]+\/?$/.test(path)
    ? "/articles"
    : ["/", "/nav", "/works", "/story", "/about", "/message"].includes(path)
    ? path
    : null;
  observer && observer.disconnect();
  exposed = new Set();
  if (!currentPath) return;
  trackSiteEvent("page_view");
  clearTimeout(scanTimer);
  scanTimer = setTimeout(scan, 100);
}

export function initSiteAnalytics(router) {
  // capture 阶段记录原页面；不拦截跳转、不触碰图片放大或表单处理。
  document.addEventListener(
    "click",
    event => {
      let node = event.target;
      while (node && node !== document.body) {
        const item = classify(node);
        if (item && item.type) {
          trackSiteEvent(item.type, item.target);
          break;
        }
        node = node.parentElement;
      }
    },
    true
  );
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
  window.addEventListener("pagehide", () => flush(true));
  if (window.IntersectionObserver) {
    observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
          const item = tracked.get(entry.target);
          if (!item) return;
          if (!exposed.has(item.target)) {
            exposed.add(item.target);
            trackSiteEvent("entry_view", item.target);
          }
          if (
            ["phone", "email", "wechat"].includes(item.target) &&
            !exposed.has("contact")
          ) {
            exposed.add("contact");
            trackSiteEvent("entry_view", "contact");
          }
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.5 }
    );
    mutationObserver = new MutationObserver(() => {
      clearTimeout(scanTimer);
      scanTimer = setTimeout(scan, 100);
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }
  router.afterEach(to => startPage(to.path));
  router.onReady(() => startPage(router.currentRoute.path));
}
